import React, { useState } from 'react'

import {
  EditUserPreferences$Request,
  GetUserPreferences$Request,
  UserCompanyDto,
  UserPreferenceDto,
  UserPreferenceType,
} from '../../services/api.types'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import { Card } from '../../components/Card/Card'
import { Box, Grid } from '@material-ui/core'
import { t } from 'ttag'
import { isRtl } from '../../i18nInit'
import { useForm } from 'react-hook-form'
import YumFormListToggle from '../../components/YumFormListItemToggle/YumFormListToggle'
import { hasFeatureFlag } from '../../common/helpers/featureFlags'

import YumHeader from '../../components/YumHeader/YumHeader'
import { gray } from '../../styles/material-ui-theme-v2'

import { ReactComponent as ChatIcon } from '../../assets/v3/icons/chat-inactive.svg'
import { ReactComponent as EmailIcon } from '../../assets/v3/icons/announcements/email.svg'
import { ReactComponent as AnnouncementIcon } from '../../assets/v3/icons/announcement.svg'
import { ReactComponent as ReviewIcon } from '../../assets/v3/icons/review.svg'
import { ReactComponent as AppIcon } from '../../assets/v3/icons/app.svg'
import Hidden from '@material-ui/core/Hidden'
import YumCancelButton from '../../components/YumCancelButton/YumCancelButton'
import YumSubmitButton from '../../components/YumSubmitButton/YumSubmitButton'
import Typography from '@material-ui/core/Typography'
import makeStyles from '@material-ui/core/styles/makeStyles'

export interface INotificationPreferencesProps {
  preferences: UserPreferenceDto[]
  isLoading: boolean
  lastUpdate: number | null
  isDuringRegistration?: boolean
  userCompanies: UserCompanyDto[]
}

export interface INotificationPreferencesDispatchProps {
  getUserPreferences(payload: GetUserPreferences$Request): void
  editUserPreferences(payload: EditUserPreferences$Request & { hasPushNotifications: boolean }): void
  cancelUserPreferences(): void
}

type NotificationPreferencesFormData = {
  //pushNotifications: boolean
  appPushNotification: boolean
  chatPushNotification: boolean
  announcementPushNotification: boolean

  //emails: boolean
  newsletterEmail: boolean
  reviewEmail: boolean
  announcementEmail: boolean
}

const getPreference = (preferences: UserPreferenceDto[], type: UserPreferenceType, optOut = false) => {
  const subscribed = preferences.find((p) => p.type === type)?.subscribed
  const defaultValue = !optOut
  return subscribed === undefined ? defaultValue : subscribed
}

const getDefaultValues = (preferences: UserPreferenceDto[], userCompanies: UserCompanyDto[]) => {
  const optOutReviews = hasFeatureFlag(userCompanies, 'optOutReviews')
  const optOutNewsletter = hasFeatureFlag(userCompanies, 'optOutNewsletter')

  const announcementPushNotification = getPreference(preferences, UserPreferenceType.ANNOUNCEMENT_PUSH_NOTIFICATION)
  const chatPushNotification = getPreference(preferences, UserPreferenceType.CHAT_PUSH_NOTIFICATION)
  const appPushNotification = getPreference(preferences, UserPreferenceType.APP_PUSH_NOTIFICATION)

  const newsletterEmail = getPreference(preferences, UserPreferenceType.NEWSLETTER_EMAIL, optOutNewsletter)
  const reviewEmail = getPreference(preferences, UserPreferenceType.REVIEW_EMAIL, optOutReviews)
  const announcementEmail = getPreference(preferences, UserPreferenceType.ANNOUNCEMENT_EMAIL)

  return {
    announcementPushNotification,
    chatPushNotification,
    appPushNotification,

    newsletterEmail,
    reviewEmail,
    announcementEmail,
  }
}

const useStyles = makeStyles((theme) => ({
  lastColumn: {
    paddingTop: '0px !important',
    paddingBottom: '0px !important',
    [theme.breakpoints.up('md')]: isRtl ? { borderRight: `2px solid ${gray}` } : { borderLeft: `2px solid ${gray}` },
  },
}))

export default function NotificationPreferences(
  props: INotificationPreferencesProps & INotificationPreferencesDispatchProps,
) {
  const classes = useStyles()
  const {
    lastUpdate,
    preferences,
    isDuringRegistration,
    userCompanies,
    getUserPreferences,
    editUserPreferences,
    cancelUserPreferences,
  } = props

  React.useEffect(() => {
    getUserPreferences({})

    if (!isDuringRegistration) {
      setTimeout(() => {
        document.body.classList.add('show-back-btn')
      }, 0)
    }
    return function cleanup() {
      if (!isDuringRegistration) {
        document.body.classList.remove('show-back-btn')
      }
    }
  }, [])

  const form = useForm<NotificationPreferencesFormData>({
    shouldUnregister: false,
    defaultValues: getDefaultValues(preferences, userCompanies),
  })
  const { handleSubmit, reset } = form

  React.useEffect(() => {
    reset(getDefaultValues(preferences, userCompanies))
  }, [preferences, userCompanies])

  const [submitInProgress, setSubmitInProgress] = useState(false)

  React.useEffect(() => {
    setSubmitInProgress(false)
  }, [lastUpdate])

  const handleCancel = () => {
    cancelUserPreferences()
  }

  const onSubmit = handleSubmit((newData) => {
    setSubmitInProgress(true)
    editUserPreferences({
      isDuringRegistration,
      hasPushNotifications:
        newData.appPushNotification || newData.chatPushNotification || newData.announcementPushNotification,
      preferences: [
        {
          id: null,
          type: UserPreferenceType.APP_PUSH_NOTIFICATION,
          subscribed: newData.appPushNotification,
        },
        {
          id: null,
          type: UserPreferenceType.CHAT_PUSH_NOTIFICATION,
          subscribed: newData.chatPushNotification,
        },
        {
          id: null,
          type: UserPreferenceType.ANNOUNCEMENT_PUSH_NOTIFICATION,
          subscribed: newData.announcementPushNotification,
        },
        {
          id: null,
          type: UserPreferenceType.NEWSLETTER_EMAIL,
          subscribed: newData.newsletterEmail,
        },
        {
          id: null,
          type: UserPreferenceType.REVIEW_EMAIL,
          subscribed: newData.reviewEmail,
        },
        {
          id: null,
          type: UserPreferenceType.ANNOUNCEMENT_EMAIL,
          subscribed: newData.announcementEmail,
        },
      ],
    })
  })

  const announcementPushNotificationField = (
    <YumFormListToggle
      label={t`Announcements`}
      description={t`Sent by organizers`}
      name="announcementPushNotification"
      icon={<AnnouncementIcon style={{ width: '1.5rem', height: '1.5rem' }} />}
      form={form}
    />
  )

  const appPushNotificationField = (
    <YumFormListToggle
      label={t`Notifications`}
      description={t`Important updates from app`}
      name="appPushNotification"
      icon={<AppIcon style={{ width: '1.5rem', height: '1.5rem' }} />}
      form={form}
    />
  )

  const chatPushNotificationField = (
    <YumFormListToggle
      label={t`Chat`}
      description={t`New chat messages`}
      name="chatPushNotification"
      icon={<ChatIcon style={{ width: '1.5rem', height: '1.5rem' }} />}
      form={form}
      mb0={true}
    />
  )

  const sendNotificationsAccordion = (
    <>
      <Typography variant="h2" style={{ marginBottom: '20px' }}>
        {t`Push notifications`}
      </Typography>
      {announcementPushNotificationField}
      {appPushNotificationField}
      {chatPushNotificationField}
    </>
  )

  const announcementEmailField = (
    <YumFormListToggle
      label={t`Announcements`}
      description={t`Sent by organizers`}
      name="announcementEmail"
      icon={<AnnouncementIcon style={{ width: '1.5rem', height: '1.5rem' }} />}
      form={form}
    />
  )

  const newsletterEmailField = (
    <YumFormListToggle
      label={t`Newsletter`}
      description={t`YuMuuv monthly newsletter`}
      name="newsletterEmail"
      icon={<EmailIcon style={{ width: '1.5rem', height: '1.5rem' }} />}
      form={form}
      mb0={true}
    />
  )

  const reviewEmailField = (
    <YumFormListToggle
      label={t`Incentivized reviews`}
      description={t`Invites for YuMuuv app reviews`}
      name="reviewEmail"
      icon={<ReviewIcon style={{ width: '1.5rem', height: '1.5rem' }} />}
      form={form}
    />
  )

  const emailsAccordion = (
    <>
      <Typography variant="h2" style={{ marginBottom: '20px' }}>
        {t`Emails`}
      </Typography>
      {announcementEmailField}
      {reviewEmailField}
      {newsletterEmailField}
    </>
  )

  const actionButtons = (
    <div style={{ display: 'flex', marginTop: '40px', gap: '24px' }}>
      {!isDuringRegistration && <YumCancelButton onClick={handleCancel} submitInProgress={submitInProgress} />}
      <YumSubmitButton text={isDuringRegistration ? t`Next` : t`Update`} submitInProgress={submitInProgress} />
    </div>
  )

  const divider = <div style={{ backgroundColor: gray, height: '2px', margin: '22px 0' }} />
  const divider1 = <div style={{ backgroundColor: gray, height: '2px', margin: '0 0px 20px' }} />
  const divider2 = <div style={{ backgroundColor: gray, height: '2px', margin: '22px 0px 0' }} />

  const renderedForm = (
    <form onSubmit={onSubmit}>
      {isDuringRegistration ? (
        <>
          {sendNotificationsAccordion}
          {divider}
          {emailsAccordion}
        </>
      ) : (
        <Grid container={true} spacing={10} style={{ marginTop: '0', marginBottom: '0' }}>
          <Grid
            item={true}
            xs={12}
            md={6}
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginLeft: 'auto',
              marginRight: 'auto',
              paddingTop: 0,
              paddingBottom: 0,
            }}
          >
            {/*<Hidden mdUp>{divider1}</Hidden>*/}
            {sendNotificationsAccordion}
            <Hidden mdUp>{divider2}</Hidden>
          </Grid>
          <Grid item={true} xs={12} md={6} className={classes.lastColumn}>
            <Box
              sx={{
                marginTop: {
                  xs: '20px',
                  md: '0px',
                },
              }}
            >
              {emailsAccordion}
            </Box>
          </Grid>
        </Grid>
      )}

      <div style={{ maxWidth: '480px', margin: '0 auto' }}>{actionButtons}</div>
    </form>
  )

  return isDuringRegistration ? (
    <>
      <Typography variant="subtitle2" style={{ marginBottom: '32px' }}>{t`Preferences`}</Typography>
      {renderedForm}
    </>
  ) : (
    <Card>
      <NotificationsWrapper />

      <YumHeader>
        <h1 style={{ marginBottom: '14px' }}>{t`Preferences`}</h1>
      </YumHeader>

      {renderedForm}
    </Card>
  )
}
